// Full legal page content — kept centralized so updates don't touch layout.
// Copy is generic-conservative; final wording should be reviewed by counsel.

const EFFECTIVE_DATE = "February 1, 2026";
const COMPANY = "Noble Strategies Consulting";

export const LEGAL_PAGES = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    eyebrow: "Legal",
    lastUpdated: EFFECTIVE_DATE,
    intro:
      `${COMPANY} ("Noble Strategies," "we," "our," or "us") respects your privacy. This Privacy Policy explains what information we collect, how we use it, and the choices available to you.`,
    sections: [
      {
        h: "1. Information We Collect",
        body:
          "We collect information that you voluntarily provide when you contact us, submit an intake or inquiry form, register for the client portal, or otherwise interact with our services. This may include your name, email address, phone number, mailing city/state, military service details, medical or benefits-related information you choose to share, and any documents you upload to the portal.\n\nWe also automatically collect limited technical information about your visit (browser type, IP address, pages viewed) via standard web logs and cookies.",
      },
      {
        h: "2. How We Use Information",
        body:
          "We use the information you provide to respond to your inquiries, coordinate consulting services, prepare and manage your client engagement, communicate with you about your matter, improve our website, and comply with legal obligations. We do not sell your personal information.",
      },
      {
        h: "3. How Information Is Shared",
        body:
          "We share information only as needed to deliver our services (e.g., with consultants working on your case), with service providers who help us operate the website (hosting, email delivery, secure storage), or when required by law. If you use our Insurance Solutions division, information you provide there may be shared with insurance carriers or underwriters strictly as required to obtain quotes or coverage.",
      },
      {
        h: "4. Data Security",
        body:
          "We use industry-standard measures — including encrypted transport (HTTPS), password hashing, and access controls — to protect your information. No system is completely secure, and you use the client portal at your own risk. Do not upload information you are not comfortable sharing electronically.",
      },
      {
        h: "5. Cookies and Analytics",
        body:
          "We use cookies to keep you signed in to the client portal and to remember your preferences. See our Cookie Notice for details.",
      },
      {
        h: "6. Your Choices",
        body:
          "You can request to review, correct, or delete your personal information at any time by emailing info@noblestrategiesconsulting.com. Deleting information may limit our ability to continue providing services.",
      },
      {
        h: "7. Retention",
        body:
          "We retain information for as long as is reasonably necessary to provide services, comply with legal obligations, resolve disputes, and enforce our agreements.",
      },
      {
        h: "8. Children",
        body:
          "Our services are not directed to children under 13, and we do not knowingly collect information from children.",
      },
      {
        h: "9. Changes to This Policy",
        body:
          "We may update this policy from time to time. The date at the top indicates the most recent revision.",
      },
      {
        h: "10. Contact",
        body:
          `Questions about privacy? Email info@noblestrategiesconsulting.com or call 786-519-7988.`,
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms of Service",
    eyebrow: "Legal",
    lastUpdated: EFFECTIVE_DATE,
    intro:
      `These Terms of Service ("Terms") govern your use of the ${COMPANY} website and services. By using our website, forms, or client portal you agree to these Terms.`,
    sections: [
      {
        h: "1. Services",
        body:
          `${COMPANY} provides consulting services in three areas: Veteran Disability Consulting, Insurance Solutions, and Education Assistance. Details of each service are described elsewhere on the website.`,
      },
      {
        h: "2. Not a Law Firm; No Legal Advice",
        body:
          `${COMPANY} is not a law firm. Nothing we say or provide constitutes legal advice or creates an attorney-client relationship. For legal representation, consult a licensed attorney.`,
      },
      {
        h: "3. Not Affiliated With the VA",
        body:
          "We are an independent, veteran-owned business. We are not affiliated with, endorsed by, or connected to the U.S. Department of Veterans Affairs.",
      },
      {
        h: "4. No Guarantees",
        body:
          "We do not guarantee any specific outcome — including VA rating decisions, insurance approvals, scholarship awards, or educational admissions. Every situation is unique and results depend on evidence, adjudication, and factors outside our control.",
      },
      {
        h: "5. Your Responsibilities",
        body:
          "You agree to provide accurate information, to protect your account credentials, to use the client portal only for lawful purposes, and to not upload malicious content or content that violates the rights of others.",
      },
      {
        h: "6. Fees",
        body:
          "Initial consultations are free. Any fees for ongoing consulting services will be disclosed and agreed to in writing before work begins.",
      },
      {
        h: "7. Intellectual Property",
        body:
          `Content on our website — including copy, guides, templates, and the Noble Strategies Personalized C&P Guide Method — is owned by or licensed to ${COMPANY}. Do not copy or redistribute without permission.`,
      },
      {
        h: "8. Limitation of Liability",
        body:
          `To the fullest extent permitted by law, ${COMPANY} is not liable for indirect, incidental, special, or consequential damages arising from your use of the website or services. Our total liability shall not exceed the fees you paid us in the six (6) months preceding the claim.`,
      },
      {
        h: "9. Termination",
        body:
          "We may suspend or terminate access to the client portal at our discretion, including for violation of these Terms.",
      },
      {
        h: "10. Governing Law",
        body:
          "These Terms are governed by the laws of the State of Florida, without regard to conflict-of-law principles.",
      },
      {
        h: "11. Contact",
        body:
          "Questions about these Terms? Email info@noblestrategiesconsulting.com.",
      },
    ],
  },

  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    eyebrow: "Legal",
    lastUpdated: EFFECTIVE_DATE,
    intro:
      `The information provided by ${COMPANY} on our website and through our services is for general informational and educational purposes only.`,
    sections: [
      {
        h: "Not Legal Advice",
        body:
          "Nothing on this website constitutes legal advice. We are not a law firm and do not provide legal representation. For legal advice about your specific situation, consult a licensed attorney.",
      },
      {
        h: "Not Medical Advice",
        body:
          "We do not provide medical advice, diagnosis, or treatment. Any references to medical conditions, symptoms, or examinations are for educational purposes only. Always consult a qualified healthcare provider.",
      },
      {
        h: "Not Affiliated With the VA",
        body:
          "We are not affiliated with, endorsed by, or connected to the U.S. Department of Veterans Affairs. Our consultants are not accredited by the VA Office of General Counsel unless separately stated in writing.",
      },
      {
        h: "No Guaranteed Outcomes",
        body:
          "VA claims, insurance underwriting, and educational program admissions all involve independent decision-makers. No consultant, attorney, or advisor can guarantee outcomes. Past results do not predict future results.",
      },
      {
        h: "Insurance Services",
        body:
          `Insurance offered through our Insurance Solutions division is provided by licensed carriers. Coverage, pricing, and availability vary by state and personal circumstances. Not all products are available in all states.`,
      },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Notice",
    eyebrow: "Legal",
    lastUpdated: EFFECTIVE_DATE,
    intro:
      "This notice explains how our website uses cookies and similar technologies.",
    sections: [
      {
        h: "What Are Cookies?",
        body:
          "Cookies are small text files stored on your device when you visit a website. They help the website remember your preferences and keep you signed in.",
      },
      {
        h: "How We Use Cookies",
        body:
          "We use a small number of cookies:\n\n• Session cookies to keep you signed in to the client portal (required — the portal will not work without these)\n• Preference cookies to remember your cookie consent choice\n\nWe do not use third-party advertising cookies or cross-site tracking.",
      },
      {
        h: "Your Choices",
        body:
          "Most browsers allow you to control cookies through settings. If you disable required cookies, some parts of the website — especially the client portal — will not function.",
      },
      {
        h: "Analytics",
        body:
          "If we add analytics in the future (e.g., to understand which pages are most useful), we will update this notice and provide a way for you to opt out.",
      },
      {
        h: "Contact",
        body:
          "Questions? Email info@noblestrategiesconsulting.com.",
      },
    ],
  },
};

export const LEGAL_LIST = Object.values(LEGAL_PAGES);
